import { EditorState, Modifier, SelectionState } from 'draft-js';

export const AUDIO_HIGHLIGHT_STYLE = 'AUDIO_PLAYING';
export const AUDIO_HIGHLIGHT_FADING_STYLE = 'AUDIO_PLAYING_FADING';

const audioHighlightTransition =
  'background-color 400ms ease, border-color 400ms ease, box-shadow 400ms ease';

export const audioHighlightStyleMap = {
  [AUDIO_HIGHLIGHT_STYLE]: {
    position: 'relative',
    zIndex: 2,
    borderBottom: '2px solid #f2711c',
    borderRadius: '3px',
    backgroundColor: 'rgba(242, 113, 28, 0.32)',
    boxShadow: '0 0 0 3px rgba(242, 113, 28, 0.16)',
    transition: audioHighlightTransition,
  },
  [AUDIO_HIGHLIGHT_FADING_STYLE]: {
    position: 'relative',
    zIndex: 2,
    borderBottom: '2px solid rgba(242, 113, 28, 0)',
    borderRadius: '3px',
    backgroundColor: 'rgba(242, 113, 28, 0)',
    boxShadow: '0 0 0 3px rgba(242, 113, 28, 0)',
    transition: audioHighlightTransition,
    animation: 'br-audio-highlight-fade 400ms ease forwards',
  },
};

export const audioFeedbackSelectionFromDraft = editorState => {
  if (!editorState) return null;
  const selection = editorState.getSelection();
  if (!selection || selection.isCollapsed()) return null;

  const content = editorState.getCurrentContent();
  const startKey = selection.getStartKey();
  const endKey = selection.getEndKey();
  const startOffset = selection.getStartOffset();
  const endOffset = selection.getEndOffset();
  const blocks = content.getBlockMap().toArray();
  const startIndex = blocks.findIndex(block => block.getKey() === startKey);
  const endIndex = blocks.findIndex(block => block.getKey() === endKey);
  if (startIndex < 0 || endIndex < startIndex) return null;

  const text = blocks
    .slice(startIndex, endIndex + 1)
    .map((block, index, selectedBlocks) => {
      const blockText = block.getText();
      const from = index === 0 ? startOffset : 0;
      const to =
        index === selectedBlocks.length - 1 ? endOffset : blockText.length;
      return blockText.slice(from, to);
    })
    .join('\n');

  if (!text) return null;
  return {
    text,
    anchor: { startKey, startOffset, endKey, endOffset },
  };
};

const removeProjectedAudioHighlight = content =>
  content.getBlockMap().reduce((nextContent, block) => {
    const hasAudioHighlight = block.getCharacterList().some(
      character =>
        character.hasStyle(AUDIO_HIGHLIGHT_STYLE) ||
        character.hasStyle(AUDIO_HIGHLIGHT_FADING_STYLE)
    );
    if (!hasAudioHighlight) return nextContent;

    const key = block.getKey();
    const blockSelection = SelectionState.createEmpty(key).merge({
      anchorKey: key,
      anchorOffset: 0,
      focusKey: key,
      focusOffset: block.getLength(),
      hasFocus: false,
      isBackward: false,
    });
    const withoutActiveHighlight = Modifier.removeInlineStyle(
      nextContent,
      blockSelection,
      AUDIO_HIGHLIGHT_STYLE
    );
    return Modifier.removeInlineStyle(
      withoutActiveHighlight,
      blockSelection,
      AUDIO_HIGHLIGHT_FADING_STYLE
    );
  }, content);

export const applyAudioHighlight = (editorState, anchor) => {
  if (!editorState) return editorState;
  const sourceContent = editorState.getCurrentContent();
  const content = removeProjectedAudioHighlight(sourceContent);
  const cleanEditorState =
    content === sourceContent
      ? editorState
      : EditorState.set(editorState, { currentContent: content });
  if (!anchor) return cleanEditorState;

  const start = content.getBlockForKey(anchor.startKey);
  const end = content.getBlockForKey(anchor.endKey);
  if (!start || !end) return cleanEditorState;
  if (
    anchor.startKey === anchor.endKey &&
    anchor.endOffset <= anchor.startOffset
  ) {
    return cleanEditorState;
  }

  const selection = SelectionState.createEmpty(anchor.startKey).merge({
    anchorKey: anchor.startKey,
    anchorOffset: anchor.startOffset,
    focusKey: anchor.endKey,
    focusOffset: anchor.endOffset,
    hasFocus: false,
    isBackward: false,
  });
  const highlightedContent = Modifier.applyInlineStyle(
    content,
    selection,
    anchor.fading ? AUDIO_HIGHLIGHT_FADING_STYLE : AUDIO_HIGHLIGHT_STYLE
  );
  return EditorState.set(cleanEditorState, {
    currentContent: highlightedContent,
  });
};
